import { useMemo } from "react";

import type {
	PlainSettingForDesignPlainQuery,
	PlainWidgetForDesignPlainQuery,
	SnackListForDesignPlainQuery,
} from "@/lib/gql/__generated__/graphql";

import * as FormModel from "@/components/features/design/plain/model";
import type * as FormModelType from "@/components/features/design/plain/model/type";

import { useActions } from "../use-actions";
import { useUpdatePlainSettingAndWidget } from "../use-update";
import * as Converter from "./converter";

type Props = {
	plainSettingForDesignPlainServerModel: PlainSettingForDesignPlainQuery;
	snackListServerModel: SnackListForDesignPlainQuery;
	plainWidgetDefaultOfServerModel: PlainWidgetForDesignPlainQuery;
};

export const useInitialized = ({
	plainSettingForDesignPlainServerModel,
	snackListServerModel,
	plainWidgetDefaultOfServerModel,
}: Props) => {
	/**
	 * QueryData の初期化
	 */
	const queryData: FormModelType.QueryData = useMemo(
		() =>
			({
				plainSetting: plainSettingForDesignPlainServerModel.plainSetting,
				hotel: plainSettingForDesignPlainServerModel.hotel,
				planList: plainSettingForDesignPlainServerModel.planList,
				roomList: plainSettingForDesignPlainServerModel.roomList,
				guestPageVisibilitySettings: plainSettingForDesignPlainServerModel.guestPageVisibilitySettings,
				plainWidgetDefault: plainWidgetDefaultOfServerModel.plainWidget,
				snackList: snackListServerModel.snackList,
			}) satisfies FormModelType.QueryData,
		[
			plainSettingForDesignPlainServerModel.guestPageVisibilitySettings,
			plainSettingForDesignPlainServerModel.hotel,
			plainSettingForDesignPlainServerModel.plainSetting,
			plainSettingForDesignPlainServerModel.planList,
			plainSettingForDesignPlainServerModel.roomList,
			plainWidgetDefaultOfServerModel,
			snackListServerModel,
		],
	);

	/**
	 * InitInputModel の初期化
	 */
	const initInputModel: FormModelType.PlainSettingFormModel["InputModel"] = useMemo(
		() =>
			Converter.serverModelToInputModelConverter(
				queryData,
			) satisfies FormModelType.PlainSettingFormModel["InputModel"],
		[queryData],
	);

	const { inputModel, viewModel, onSetViewModel, onResetViewModel } =
		FormModel.usePlainSettingFormModel(initInputModel);

	const {
		// URL の設定
		onChangeDomain,

		// 色の設定
		onChangeColorBackground,
		onChangeColorFont,
		onChangeColorPrimary,
		onApplyRecommendationColors,

		// 施設情報の表示設定
		onChangeLogo,
		onChangeLogoSize,
		onChangeIsShowHotelName,
		onChangeIsShowHotelLogo,
		onChangeIsShowHotelCatchCopy,
		onChangeIsShowHotelKeyVisual,

		// フォント設定
		onChangeFontFamily,

		// ReservationComponent の設定
		onChangeReservationComponentType,

		// スナックの設定
		onAddPlainWidgetDefault,
		onRemovePlainWidgetDefault,
		onChangeWidgetDecoration,
		onUpdateOrderPlainWidgets,
		// マガジン記事の表示設定
		onChangeIsShowMagazineAbout,
		onChangeIsShowMagazinePlan,
		onChangeIsShowMagazineRoom,

		// 分析
		onChangeGtmID,

		// 検索エンジンへの表示
		onChangeGuestPageUnlisted,
	} = useActions({
		viewModel,
		onSetViewModel,
		savedGuestPageVisibility: queryData.guestPageVisibilitySettings.plainVisibility,
	});
	const { onUpdatePlainSettingAndWidget } = useUpdatePlainSettingAndWidget(
		inputModel,
		queryData.guestPageVisibilitySettings,
		onResetViewModel,
	);

	return {
		// readonly
		queryData,
		viewModel,

		// mutation
		onUpdatePlainSettingAndWidget,

		// actions
		// URL の設定
		onChangeDomain,

		// 色の設定
		onChangeColorBackground,
		onChangeColorFont,
		onChangeColorPrimary,
		onApplyRecommendationColors,

		// ホテルの表示設定
		onChangeLogo,
		onChangeLogoSize,
		onChangeIsShowHotelName,
		onChangeIsShowHotelLogo,
		onChangeIsShowHotelCatchCopy,
		onChangeIsShowHotelKeyVisual,

		// フォント設定
		onChangeFontFamily,

		// ReservationComponent の設定
		onChangeReservationComponentType,

		// スナックの設定
		onAddPlainWidgetDefault,
		onRemovePlainWidgetDefault,
		onChangeWidgetDecoration,
		onUpdateOrderPlainWidgets,

		// マガジン記事の表示設定
		onChangeIsShowMagazineAbout,
		onChangeIsShowMagazinePlan,
		onChangeIsShowMagazineRoom,

		// 分析
		onChangeGtmID,

		// 検索エンジンへの表示
		onChangeGuestPageUnlisted,
	};
};
